import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import { t } from "../i18n";
import { navigate } from "../utils/navigate";

const WHY = [
  { id: "bp", key: "whyBp" },
  { id: "sugar", key: "whySugar" },
  { id: "infection", key: "whyInfection" },
  { id: "pain", key: "whyPain" },
];

function matchesMed(m, q) {
  if (!q) return true;
  const hay = `${m.brand} ${m.salt} ${m.strength} ${m.category}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((part) => hay.includes(part));
}

export default function WriteRx({ lang }) {
  const { token } = useAuth();
  const [meds, setMeds] = useState([]);
  const [patients, setPatients] = useState([]);
  const [find, setFind] = useState("");
  const [picked, setPicked] = useState(null);
  const [patientId, setPatientId] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [dose, setDose] = useState("");
  const [days, setDays] = useState("30");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [saving, setSaving] = useState(false);
  const [given, setGiven] = useState(0);

  useEffect(() => {
    api("/api/medicines", { token }).then((d) => setMeds(d.medicines || []));
    api("/api/patients", { token })
      .then((d) => setPatients(d.patients || []))
      .catch(() => {});
  }, [token]);

  const hits = useMemo(() => meds.filter((m) => matchesMed(m, find)).slice(0, 8), [meds, find]);

  const pickMed = (m) => {
    setPicked(m);
    setFind("");
    setDose(m.suggested_dose || "");
    setDays(String(m.suggested_days || 7));
    const whyKey = { "Blood pressure": "whyBp", Sugar: "whySugar", Infection: "whyInfection", Pain: "whyPain" }[
      m.suggested_reason
    ];
    setReason(whyKey ? t(lang, whyKey) : m.suggested_reason || reason);
    setErr("");
  };

  const pickPatient = (p) => {
    setPatientId(String(p.id));
    setPhone(p.phone || "");
    setName(p.name || "");
  };

  const submit = async () => {
    setErr("");
    setOk("");
    if (!picked) {
      setErr(t(lang, "pickMedicine"));
      return;
    }
    if (!patientId && !phone) {
      setErr(t(lang, "patientPhone"));
      return;
    }
    setSaving(true);
    try {
      const body = {
        medicine_id: Number(picked.id),
        dose: dose || picked.suggested_dose,
        days: Number(days) || picked.suggested_days || 7,
        reason,
      };
      if (patientId) body.patient_id = Number(patientId);
      else {
        body.patient_phone = phone;
        body.patient_name = name;
      }
      const res = await api("/api/prescriptions", { method: "POST", token, body });
      if (res.patient?.id) {
        setPatientId(String(res.patient.id));
        setPhone(res.patient.phone || phone);
        setName(res.patient.name || name);
      }
      setGiven((n) => n + 1);
      setOk(`${picked.brand} · ${picked.salt}`);
      setPicked(null);
      setDose("");
      setFind("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tt-page">
      <h2>{t(lang, "writeRx")}</h2>
      <p className="tt-muted">{t(lang, "writeFastHint")}</p>

      <h3>{t(lang, "patientPhone")}</h3>
      {!!patients.length && (
        <div className="tt-chips">
          {patients.slice(0, 6).map((p) => (
            <button
              key={p.id}
              type="button"
              className={patientId === String(p.id) ? "on" : ""}
              onClick={() => pickPatient(p)}
            >
              {p.name || p.phone}
            </button>
          ))}
        </div>
      )}
      <input
        type="tel"
        inputMode="numeric"
        placeholder={t(lang, "patientPhone")}
        value={phone}
        onChange={(e) => {
          setPhone(e.target.value);
          setPatientId("");
        }}
      />
      {!patientId && (
        <input
          placeholder={t(lang, "patientName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      )}

      <h3>{t(lang, "pickMedicine")}</h3>
      {picked ? (
        <button type="button" className="tt-rx" onClick={() => setPicked(null)}>
          <strong>
            {picked.brand} · {picked.salt} {picked.strength}
          </strong>
          <span className="tt-muted">{t(lang, "change")}</span>
        </button>
      ) : (
        <>
          <input
            placeholder={t(lang, "findPack")}
            value={find}
            onChange={(e) => setFind(e.target.value)}
            autoComplete="off"
          />
          <div className="tt-suggest">
            {(find ? hits : meds.slice(0, 8)).map((m) => (
              <button key={m.id} type="button" onClick={() => pickMed(m)}>
                <strong>
                  {m.brand} · {m.salt} {m.strength}
                </strong>
              </button>
            ))}
          </div>
        </>
      )}

      {picked && (
        <>
          <input placeholder={t(lang, "dose")} value={dose} onChange={(e) => setDose(e.target.value)} />
          <input
            type="number"
            min="1"
            placeholder={t(lang, "days")}
            value={days}
            onChange={(e) => setDays(e.target.value)}
          />
          <p>{t(lang, "why")}</p>
          <div className="tt-chips">
            {WHY.map((w) => (
              <button
                key={w.id}
                type="button"
                className={reason === t(lang, w.key) ? "on" : ""}
                onClick={() => setReason(t(lang, w.key))}
              >
                {t(lang, w.key)}
              </button>
            ))}
          </div>
        </>
      )}

      {err && <p className="tt-err">{err}</p>}
      {ok && (
        <p className="tt-ok">
          {t(lang, "givenRx")} {ok}
          {given > 0 ? ` · ${given}` : ""}
        </p>
      )}
      <button className="tt-btn" onClick={submit} disabled={saving}>
        {saving ? "…" : given ? t(lang, "addAnother") : t(lang, "write")}
      </button>
      {given > 0 && (
        <button className="tt-btn ghost" onClick={() => navigate("/board")}>
          {t(lang, "board")}
        </button>
      )}
      <p className="tt-foot">{t(lang, "neverStop")}</p>
    </div>
  );
}
